import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Button, DatePicker, Form, Input, Modal, Popconfirm, Radio, Select, Switch } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { apiErrorMessage } from '../api/client';
import { categoriesApi, operationsApi, skladsApi, storesApi } from '../api/endpoints';
import { invalidateMoneyData, queryKeys } from '../api/queryKeys';
import { PAYMENT_METHODS } from '../api/types';
import type { Operation, PaymentMethod, UpdateOperationBody } from '../api/types';
import { kopeksToRubString, rubToKopeks } from '../lib/money';
import { TYPE_FIELDS } from '../lib/operationShape';
import { AmountInput, positiveAmountRule } from './AmountInput';
import { ModalSelect } from './ModalSelect';
import { OpTypeTag } from './OpTypeTag';

interface EditFormValues {
  date: Dayjs;
  amount: string;
  storeId?: string;
  skladId?: string;
  categoryId?: string;
  paymentMethod?: PaymentMethod;
  deductFromStoreBalance?: boolean;
  comment?: string;
}

/**
 * Edit modal shared by ledgers and the journal (STEP-06/07). Type is fixed —
 * the backend deliberately rejects type changes (delete + recreate instead).
 * Directory selects include archived entities: an old operation may point at
 * an archived store and must stay editable.
 */
export function OperationEditModal({
  operation,
  onClose,
}: {
  operation: Operation | null;
  onClose: () => void;
}): JSX.Element | null {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<EditFormValues>();
  // The «вычесть из остатка» flag needs a store, so it makes the store required.
  const deductOn = Form.useWatch('deductFromStoreBalance', form);

  const open = operation !== null;
  const storesQuery = useQuery({
    queryKey: queryKeys.stores(true),
    queryFn: () => storesApi.list(true),
    enabled: open,
  });
  const skladsQuery = useQuery({
    queryKey: queryKeys.sklads(true),
    queryFn: () => skladsApi.list(true),
    enabled: open,
  });
  const categoriesQuery = useQuery({
    queryKey: queryKeys.categories(true),
    queryFn: () => categoriesApi.list(true),
    enabled: open,
  });

  const updateMutation = useMutation({
    mutationFn: (values: EditFormValues) => {
      const fields = TYPE_FIELDS[operation!.type];
      const body: UpdateOperationBody = {
        date: values.date.format('YYYY-MM-DD'),
        amount: rubToKopeks(values.amount),
        comment: values.comment?.trim() || null,
      };
      if (fields.store === 'required') body.storeId = values.storeId;
      if (fields.store === 'optional') body.storeId = values.storeId ?? null;
      if (fields.sklad) body.skladId = values.skladId;
      if (fields.category) body.categoryId = values.categoryId;
      if (operation!.type === 'EXPENSE') {
        body.deductFromStoreBalance = values.deductFromStoreBalance ?? false;
      }
      // Old rows may carry no method — only send it once the owner picked one.
      if (operation!.type === 'STORE_PAYMENT' && values.paymentMethod) {
        body.paymentMethod = values.paymentMethod;
      }
      return operationsApi.update(operation!.id, body);
    },
    onSuccess: () => {
      message.success(t('editOp.saved'));
      invalidateMoneyData(queryClient);
      onClose();
    },
    onError: (error) => message.error(apiErrorMessage(error) ?? t('common.requestError')),
  });

  const deleteMutation = useMutation({
    mutationFn: () => operationsApi.remove(operation!.id),
    onSuccess: () => {
      message.success(t('ledger.deleted'));
      invalidateMoneyData(queryClient);
      onClose();
    },
    onError: (error) => message.error(apiErrorMessage(error) ?? t('common.requestError')),
  });

  if (!operation) return null;

  // A накладная-backed operation is document-owned: its amount equals the
  // накладная's line sums and can only change by re-pricing the doc. Offer the
  // накладная link instead of an editable (and backend-rejected) form.
  if (operation.shipmentId) {
    return (
      <Modal
        open
        onCancel={onClose}
        title={<OpTypeTag type={operation.type} />}
        destroyOnClose
        footer={[
          <Button key="cancel" onClick={onClose}>
            {t('common.cancel')}
          </Button>,
          <Button
            key="open"
            type="primary"
            onClick={() => {
              onClose();
              navigate(`/shipments/${operation.shipmentId}`);
            }}
          >
            {t('shipments.openDoc')}
          </Button>,
        ]}
      >
        {t('shipments.fromShipmentHint')}
      </Modal>
    );
  }
  const fields = TYPE_FIELDS[operation.type];

  return (
    <Modal
      open
      onCancel={onClose}
      title={<OpTypeTag type={operation.type} />}
      destroyOnClose
      footer={[
        <Popconfirm
          key="delete"
          title={t('ledger.deleteConfirmTitle')}
          description={t('ledger.deleteConfirmText')}
          okText={t('common.delete')}
          cancelText={t('common.cancel')}
          okButtonProps={{ danger: true, loading: deleteMutation.isPending }}
          onConfirm={() => deleteMutation.mutate()}
        >
          <Button danger style={{ float: 'left' }}>
            {t('common.delete')}
          </Button>
        </Popconfirm>,
        <Button key="cancel" onClick={onClose}>
          {t('common.cancel')}
        </Button>,
        <Button
          key="save"
          type="primary"
          loading={updateMutation.isPending}
          onClick={() => form.submit()}
        >
          {t('common.save')}
        </Button>,
      ]}
    >
      <Form<EditFormValues>
        form={form}
        key={operation.id}
        layout="vertical"
        requiredMark={false}
        initialValues={{
          date: dayjs(operation.date, 'YYYY-MM-DD'),
          amount: kopeksToRubString(operation.amount),
          storeId: operation.storeId ?? undefined,
          skladId: operation.skladId ?? undefined,
          categoryId: operation.categoryId ?? undefined,
          paymentMethod: operation.paymentMethod ?? undefined,
          deductFromStoreBalance: operation.deductFromStoreBalance,
          comment: operation.comment ?? undefined,
        }}
        onFinish={(values) => updateMutation.mutate(values)}
      >
        {fields.sklad ? (
          <Form.Item
            name="skladId"
            label={t('common.sklad')}
            rules={[{ required: true, message: t('common.skladRequired') }]}
          >
            <ModalSelect
              size="large"
              title={t('common.sklad')}
              options={(skladsQuery.data ?? []).map((s) => ({ value: s.id, label: s.name }))}
              loading={skladsQuery.isLoading}
            />
          </Form.Item>
        ) : null}
        {fields.category ? (
          <Form.Item
            name="categoryId"
            label={t('common.category')}
            rules={[{ required: true, message: t('common.categoryRequired') }]}
          >
            <Select
              size="large"
              options={(categoriesQuery.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
              loading={categoriesQuery.isLoading}
            />
          </Form.Item>
        ) : null}
        {fields.store !== 'none'
          ? (() => {
              const storeRequired =
                fields.store === 'required' || (operation.type === 'EXPENSE' && deductOn);
              return (
                <Form.Item
                  name="storeId"
                  label={storeRequired ? t('common.store') : t('quick.storeOptional')}
                  rules={
                    storeRequired ? [{ required: true, message: t('common.storeRequired') }] : []
                  }
                >
                  <ModalSelect
                    size="large"
                    title={t('common.store')}
                    options={(storesQuery.data ?? []).map((s) => ({ value: s.id, label: s.name }))}
                    loading={storesQuery.isLoading}
                    allowClear={!storeRequired}
                  />
                </Form.Item>
              );
            })()
          : null}
        {operation.type === 'EXPENSE' ? (
          <Form.Item
            name="deductFromStoreBalance"
            label={t('quick.deductFromStore')}
            tooltip={t('quick.deductFromStoreHint')}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        ) : null}
        <Form.Item name="amount" label={t('common.amount')} rules={[positiveAmountRule(t)]}>
          <AmountInput />
        </Form.Item>
        {operation.type === 'STORE_PAYMENT' ? (
          <Form.Item name="paymentMethod" label={t('payMethod.label')}>
            <Radio.Group
              size="large"
              optionType="button"
              buttonStyle="solid"
              style={{ width: '100%', display: 'flex' }}
              options={PAYMENT_METHODS.map((method) => ({
                value: method,
                label: t(`payMethod.${method}`),
                style: { flex: 1, textAlign: 'center' as const },
              }))}
            />
          </Form.Item>
        ) : null}
        <Form.Item
          name="date"
          label={t('common.date')}
          rules={[{ required: true, message: t('common.dateRequired') }]}
        >
          <DatePicker
            size="large"
            style={{ width: '100%' }}
            format="DD.MM.YYYY"
            allowClear={false}
            inputReadOnly
            // Body-mounted (overrides the app-wide getPopupContainer): inside
            // the modal's container the panel can open past the viewport top,
            // hiding the month/year header on short mobile screens.
            getPopupContainer={() => document.body}
          />
        </Form.Item>
        <Form.Item name="comment" label={t('common.comment')}>
          <Input size="large" placeholder={t('common.commentPlaceholder')} maxLength={1000} autoComplete="off" />
        </Form.Item>
      </Form>
    </Modal>
  );
}
